import React, { createContext, useState, useContext, useEffect } from 'react';
import apiClient from '../api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

const AuthProviderInner = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      return parsed.user || parsed;
    }
    return null;
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      const parsed = JSON.parse(storedUser);
      setUser(parsed.user || parsed);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);

    // Interceptor для обработки 401 ошибок (истекший токен)
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && window.location.pathname !== '/login') {
          // Токен истек или недействителен
          console.log('Сессия истекла. Перенаправление на страницу входа...');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          delete apiClient.defaults.headers.common['Authorization'];
          const sessionMessage = 'Сессия истекла. Пожалуйста, войдите снова.';
          console.log('Navigating to /login with message:', sessionMessage);
          localStorage.setItem('sessionExpiredMessage', sessionMessage);
          navigate('/login', { replace: true });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { token, user } = response.data;
      
      const userData = user.user || user;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(token);
      setUser(userData);
      navigate('/');
      return true;
    } catch (error) {
      console.error('Ошибка входа:', error.response ? error.response.data : error.message);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      delete apiClient.defaults.headers.common['Authorization'];
      return false;
    }
  };

  const register = async (email, password, user_name) => {
    try {
      const response = await apiClient.post('/auth/register', { email, password, user_name });
      const { token, user } = response.data;
      
      const userData = user.user || user;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(token);
      setUser(userData);
      navigate('/');
      return true;
    } catch (error) {
      console.error('Ошибка регистрации:', error.response ? error.response.data : error.message);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete apiClient.defaults.headers.common['Authorization'];
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, register, logout, loading, isAuthenticated: !!token }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }) => {
  return <AuthProviderInner>{children}</AuthProviderInner>;
};

export const useAuth = () => useContext(AuthContext);
